import React, { useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Eye, ShieldCheck, ShieldOff, KeyRound, Search, Plus, AlertCircle,
  Mail, Trash2, UserPlus,
} from "lucide-react";

// View-only permissions allowed for special_access users.
// Mirrors backend SPECIAL_ACCESS_PERMISSION_KEYS.
const VIEW_PERMISSION_GROUPS = [
  { label: "Students", key: "students_view", description: "Student list, profiles, enrolment details." },
  { label: "Student Accounts", key: "student_accounts_view", description: "Student account ledger entries (read-only)." },
  { label: "Statement of Account", key: "statement_of_account_view", description: "Bulk-print outstanding balances by section or student (read-only)." },
  { label: "Grading Setup", key: "grading_setup_view", description: "Grading periods, components, categories (read-only)." },
  { label: "Grades", key: "grades_view", description: "Assessment tasks, scores, direct grades, term comments (read-only)." },
  { label: "Report Cards", key: "report_cards_view", description: "Aggregated student report cards (read-only)." },
  { label: "Progress Feedback", key: "progress_feedback_view", description: "Teacher progress notes for parents (read-only)." },
  { label: "Employees", key: "employees_view", description: "Employee list and profiles (read-only)." },
  { label: "Employee Other Deductions", key: "employee_deductions_view", description: "Other deductions list + payment ledger (read-only)." },
  { label: "Employee Attendance", key: "employee_attendance_view", description: "Daily/monthly employee attendance grid (read-only)." },
  { label: "Employee Salary Module", key: "salary_view", description: "Salary labels and per-employee entries (read-only)." },
  { label: "Employee Salary Formula", key: "salary_formula_view", description: "Per-condition formula cells (read-only)." },
  { label: "Pay Slip", key: "payslip_view", description: "Per-employee Pay Slip preview + manual adjustments (read-only)." },
  { label: "Pay Slip Overview", key: "payslip_overview_view", description: "Branch-wide Pay Slip overview snapshot (read-only)." },
  { label: "Pay Slip History", key: "payslip_history_view", description: "Immutable Pay Slip snapshots (read-only)." },
  { label: "Classes & Sections", key: "classes_view", description: "Class list, sections, tuition fees (read-only)." },
  { label: "Supplies & Inventory", key: "inventory_view", description: "Inventory items with categories and stock counts (read-only)." },
  { label: "Events", key: "events_view", description: "Event list with dates, fees, and quantities (read-only)." },
  { label: "Parent Portal", key: "parent_portal_view", description: "Parent visibility settings (read-only)." },
  { label: "Tuition", key: "tuition_view", description: "Tuition plans, installments, paid status." },
  { label: "Attendance", key: "attendance_view", description: "Class + individual session attendance (read-only)." },
  { label: "Accounting · Income", key: "accounting_income_view", description: "Income page (payments received + manual)." },
  { label: "Accounting · Expenses", key: "accounting_expenses_view", description: "Expenses page (salaries, advances, manual)." },
  { label: "Cash Analysis", key: "cash_analysis_view", description: "Daily cash-on-hand summary." },
  { label: "Reports", key: "reports_view", description: "Income/expense reports (SY, monthly, weekly, custom)." },
];
const ALL_KEYS = VIEW_PERMISSION_GROUPS.map((p) => p.key);
const emptyPerms = () => Object.fromEntries(ALL_KEYS.map((k) => [k, false]));

export default function SpecialAccessPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/special-access");
      setRows(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return rows;
    return rows.filter((r) => [r.full_name, r.email].filter(Boolean).join(" ").toLowerCase().includes(ql));
  }, [rows, q]);

  const toggleActive = async (row) => {
    try {
      await api.patch(`/special-access/${row.user_id}`, { is_active: !row.is_active });
      toast.success(row.is_active ? "Access disabled" : "Access enabled");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/special-access/${confirmDelete.user_id}`);
      toast.success(`${confirmDelete.full_name}'s access removed`);
      setConfirmDelete(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground tracking-tight flex items-center gap-2">
            <Eye className="w-7 h-7 text-brand-purple" /> Special Access
          </h1>
          <p className="text-brand-inkMuted mt-0.5 text-sm max-w-2xl">
            Grant view-only access to people outside your staff — auditors, parent representatives, investors, accountants.
            Special access users <span className="font-semibold text-foreground">can never create, edit, or delete</span> data.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-inkMuted" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email" className="rounded-2xl h-11 pl-9 border-brand-purple/20" data-testid="sa-search" />
          </div>
          <Button onClick={() => { setEditing(null); setOpenForm(true); }} className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white h-11 px-5" data-testid="sa-add-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add Special Access
          </Button>
        </div>
      </div>

      <section className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm overflow-hidden" data-testid="sa-list">
        {loading ? (
          <div className="p-10 text-center text-brand-inkMuted">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center" data-testid="sa-empty">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-[#E3F2FD] flex items-center justify-center mb-3">
              <UserPlus className="w-7 h-7 text-[#1565C0]" />
            </div>
            <p className="font-heading text-lg font-semibold text-foreground">
              {q ? "No matches" : "No special access users yet"}
            </p>
            <p className="text-brand-inkMuted mt-1 text-sm">
              {q ? "Try a different search term." : "Click \"Add Special Access\" to invite an auditor, parent, or investor with view-only access."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F1F5FF] text-brand-inkMuted text-[11px]">
                <tr>
                  <th className="text-left px-4 py-3 font-heading font-semibold tracking-wider">FULL NAME</th>
                  <th className="text-left px-3 py-3 font-heading font-semibold tracking-wider">EMAIL</th>
                  <th className="text-center px-3 py-3 font-heading font-semibold tracking-wider">VIEW PERMISSIONS</th>
                  <th className="text-center px-3 py-3 font-heading font-semibold tracking-wider">STATUS</th>
                  <th className="text-right px-3 py-3 font-heading font-semibold tracking-wider">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.user_id} className="border-t border-brand-purple/5 hover:bg-[#FAFBFE]" data-testid={`sa-row-${r.user_id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-full bg-[#E3F2FD] text-[#1565C0] font-heading font-bold flex items-center justify-center">
                          {(r.full_name || "?").charAt(0).toUpperCase()}
                        </div>
                        <span className="font-semibold text-foreground">{r.full_name}</span>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1.5 text-foreground">
                        <Mail className="w-3.5 h-3.5 text-brand-inkMuted" />
                        <span className="truncate max-w-[220px]">{r.email}</span>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className="inline-block bg-[#E3F2FD] text-[#1565C0] text-xs font-semibold px-2.5 py-0.5 rounded-full" data-testid={`sa-perms-count-${r.user_id}`}>
                        {r.permissions_count} / {ALL_KEYS.length}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className={`inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                        r.is_active ? "bg-[#E8F5E9] text-[#2E7D32]" : "bg-[#FFEBEE] text-[#C62828]"
                      }`}>
                        {r.is_active ? <><ShieldCheck className="w-3 h-3" /> Active</> : <><ShieldOff className="w-3 h-3" /> Disabled</>}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => toggleActive(r)} className="rounded-full border-brand-purple/30 text-xs h-8" data-testid={`sa-toggle-${r.user_id}`}>
                          {r.is_active ? "Disable" : "Enable"}
                        </Button>
                        <Button size="sm" onClick={() => { setEditing(r); setOpenForm(true); }} className="rounded-full bg-brand-purple hover:bg-brand-purpleHover text-white text-xs h-8" data-testid={`sa-manage-${r.user_id}`}>
                          <KeyRound className="w-3.5 h-3.5 mr-1" /> Manage
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setConfirmDelete(r)} className="rounded-full border-[#C62828]/40 text-[#C62828] hover:bg-[#FFEBEE] text-xs h-8" data-testid={`sa-delete-${r.user_id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <SpecialAccessDialog
        open={openForm}
        onOpenChange={(o) => { setOpenForm(o); if (!o) { setEditing(null); load(); } }}
        existing={editing}
      />

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="rounded-3xl border-brand-purple/20" data-testid="sa-confirm-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-2xl">Revoke {confirmDelete?.full_name}'s access?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes their login. They will no longer be able to sign in or view any data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full border-brand-purple/30">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="rounded-full bg-[#C62828] hover:bg-[#B71C1C] text-white" data-testid="sa-confirm-delete-btn">Revoke</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function SpecialAccessDialog({ open, onOpenChange, existing }) {
  const [form, setForm] = useState({ full_name: "", email: "", password: "" });
  const [perms, setPerms] = useState(emptyPerms());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (existing) {
      setForm({ full_name: existing.full_name || "", email: existing.email || "", password: "" });
      setPerms({ ...emptyPerms(), ...(existing.permissions || {}) });
    } else {
      setForm({ full_name: "", email: "", password: "" });
      setPerms(emptyPerms());
    }
  }, [open, existing]);

  const togglePerm = (k) => setPerms((p) => ({ ...p, [k]: !p[k] }));
  const setAll = (val) => setPerms(Object.fromEntries(ALL_KEYS.map((k) => [k, val])));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim()) { toast.error("Full name is required"); return; }
    if (!existing && (!form.email || !form.password)) { toast.error("Email and password are required"); return; }
    if (form.password && form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      if (existing) {
        const body = { full_name: form.full_name.trim(), permissions: perms };
        if (form.password) body.password = form.password;
        await api.patch(`/special-access/${existing.user_id}`, body);
        toast.success("Special access updated");
      } else {
        await api.post("/special-access", {
          full_name: form.full_name.trim(),
          email: form.email.trim().toLowerCase(),
          password: form.password,
          permissions: perms,
        });
        toast.success(`${form.full_name} can now log in with view-only access`);
      }
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl border-brand-purple/20 max-w-xl max-h-[90vh] overflow-y-auto" data-testid="sa-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <Eye className="w-5 h-5 text-brand-purple" />
            {existing ? "Edit Special Access" : "Grant Special Access"}
          </DialogTitle>
          <DialogDescription>
            View-only outsider login. Edit, create, and delete operations are <span className="font-semibold text-foreground">never permitted</span> for this user type — only the modules you tick below.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="font-heading text-sm">Full Name <span className="text-[#C62828]">*</span></Label>
              <Input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="rounded-2xl h-11 border-brand-purple/20 mt-1.5" data-testid="sa-full-name" />
            </div>
            <div>
              <Label className="font-heading text-sm">Email {!existing && <span className="text-[#C62828]">*</span>}</Label>
              <Input type="email" required={!existing} disabled={!!existing} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-2xl h-11 border-brand-purple/20 mt-1.5 disabled:bg-brand-purpleLight/30" data-testid="sa-email" />
            </div>
          </div>
          <div>
            <Label className="font-heading text-sm">{existing ? "New password (optional)" : <>Password <span className="text-[#C62828]">*</span></>}</Label>
            <Input type="password" required={!existing} minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="At least 6 characters" className="rounded-2xl h-11 border-brand-purple/20 mt-1.5" data-testid="sa-password" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="font-heading">View Permissions</Label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setAll(true)} className="text-[11px] font-semibold uppercase tracking-wider text-brand-purpleHover hover:text-brand-purple" data-testid="sa-perms-all">Grant all</button>
                <span className="text-brand-inkMuted text-xs">·</span>
                <button type="button" onClick={() => setAll(false)} className="text-[11px] font-semibold uppercase tracking-wider text-brand-inkMuted hover:text-foreground" data-testid="sa-perms-none">Clear</button>
              </div>
            </div>
            <div className="space-y-2">
              {VIEW_PERMISSION_GROUPS.map((p) => (
                <label key={p.key} className={`flex items-start gap-3 rounded-2xl border px-3 py-2.5 cursor-pointer transition-colors ${
                  perms[p.key] ? "border-brand-purple bg-brand-purpleLight/40" : "border-brand-purple/15 hover:bg-[#FAFBFE]"
                }`} data-testid={`sa-perm-toggle-${p.key}`}>
                  <input type="checkbox" checked={!!perms[p.key]} onChange={() => togglePerm(p.key)} className="w-4 h-4 accent-brand-purple mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="font-heading font-semibold text-sm text-foreground">{p.label}</p>
                    <p className="text-xs text-brand-inkMuted">{p.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full border-brand-purple/30">Cancel</Button>
            <Button type="submit" disabled={busy} className="rounded-full bg-brand-purple hover:bg-brand-purpleHover text-white" data-testid="sa-submit-btn">
              {busy ? "Saving..." : existing ? "Save Changes" : "Grant Access"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
